import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import type { Product } from '@/types/database'

export function useFeaturedProduct() {
  const [product, setProduct] = useState<Product | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function fetchFeatured() {
      setIsLoading(true)
      // Use maybeSingle() to return null instead of 406 error when no featured product
      const { data, error } = await supabase
        .from('current_featured_product')
        .select('*')
        .maybeSingle()

      if (error) {
        setError(error.message)
      }
      setProduct(data as Product | null)
      setIsLoading(false)
    }

    fetchFeatured()

    // Subscribe to changes on the products table for real-time updates
    const channel = supabase
      .channel('featured-product-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'products',
          filter: 'status=eq.featured',
        },
        () => {
          // Refetch when any featured product changes
          fetchFeatured()
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [])

  return { product, isLoading, error }
}

export function useArchiveProducts() {
  const [products, setProducts] = useState<Product[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function fetch() {
      setIsLoading(true)
      const { data, error } = await supabase
        .from('archive_products')
        .select('*')
        .order('featured_end_date', { ascending: false })

      if (error) {
        setError(error.message)
      }
      setProducts((data as Product[]) || [])
      setIsLoading(false)
    }

    fetch()
  }, [])

  return { products, isLoading, error }
}

export function useProductBySlug(slug: string | undefined) {
  const [product, setProduct] = useState<Product | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!slug) {
      setIsLoading(false)
      return
    }

    async function fetch() {
      setIsLoading(true)
      // Use maybeSingle() to return null instead of 406 error when slug not found
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('slug', slug!)
        .in('status', ['featured', 'archived'])
        .maybeSingle()

      if (error) {
        setError(error.message)
      }

      // Treat expired featured products as archived so pricing logic
      // applies archive discount instead of featured discount
      let product = data as Product | null
      if (
        product &&
        product.status === 'featured' &&
        product.featured_end_date &&
        new Date(product.featured_end_date) < new Date()
      ) {
        product = { ...product, status: 'archived' as Product['status'] }
      }

      setProduct(product)
      setIsLoading(false)
    }

    fetch()
  }, [slug])

  return { product, isLoading, error }
}

export function useAllProducts() {
  const [products, setProducts] = useState<Product[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refetch = async () => {
    setIsLoading(true)
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .order('created_at', { ascending: false })

    if (error) {
      setError(error.message)
    }
    setProducts((data as Product[]) || [])
    setIsLoading(false)
  }

  useEffect(() => {
    refetch()
  }, [])

  return { products, isLoading, error, refetch }
}

export function useScheduledProducts() {
  const [products, setProducts] = useState<Product[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function fetch() {
      setIsLoading(true)
      const { data, error } = await supabase
        .from('scheduled_products_queue')
        .select('*')
        .order('featured_start_date', { ascending: true })

      if (error) {
        setError(error.message)
      }
      setProducts((data as Product[]) || [])
      setIsLoading(false)
    }

    fetch()
  }, [])

  return { products, isLoading, error }
}

// Deprecated aliases for backward compatibility
/** @deprecated Use useFeaturedProduct instead */
export const useFeaturedProduction = useFeaturedProduct

/** @deprecated Use useArchiveProducts instead */
export const useArchiveProductions = useArchiveProducts

/** @deprecated Use useProductBySlug instead */
export const useProductionBySlug = useProductBySlug

/** @deprecated Use useAllProducts instead */
export const useAllProductions = useAllProducts
